/**
 * PrintLayout Component
 *
 * Reusable layout for print-optimized pages.
 * Provides consistent styling, header, footer, and print controls.
 * Can be used for any printable content like scoring forms, matrices, reports, etc.
 */

import { useEffect, type ReactNode } from 'react';

export interface PrintLayoutProps {
  /** Page title shown in header */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Main content to print */
  children: ReactNode;
  /** Whether to auto-trigger print dialog on load */
  autoPrint?: boolean;
  /** Delay before auto-print in ms (default: 500) */
  autoPrintDelay?: number;
  /** Page orientation (default: portrait) */
  orientation?: 'portrait' | 'landscape';
  /** Whether data is still loading */
  isLoading?: boolean;
  /** Loading message */
  loadingMessage?: string;
  /** Error message if data failed to load */
  error?: string;
  /** Callback when back/close is clicked */
  onClose?: () => void;
  /** Optional footer content */
  footerContent?: ReactNode;
  /** Show generation date in header (default: true) */
  showDate?: boolean;
}

export function PrintLayout({
  title,
  subtitle,
  children,
  autoPrint = true,
  autoPrintDelay = 500,
  orientation = 'portrait',
  isLoading = false,
  loadingMessage = 'Voorbereiden...',
  error,
  onClose,
  footerContent,
  showDate = true,
}: PrintLayoutProps) {
  // Auto-trigger print dialog when data is loaded
  useEffect(() => {
    if (!isLoading && !error && autoPrint) {
      const timer = setTimeout(() => {
        window.print();
      }, autoPrintDelay);
      return () => clearTimeout(timer);
    }
  }, [isLoading, error, autoPrint, autoPrintDelay]);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-red-600">Fout</h1>
          <p className="mt-2 text-gray-600">{error}</p>
          {onClose && (
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Terug
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  const isLandscape = orientation === 'landscape';

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print { display: none !important; }
          .print-page {
            padding: 0 !important;
            margin: 0 !important;
          }
          /* Prevent elements from being split across pages */
          .print-card, .print-row {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          /* Keep section headers with their content */
          .print-section-header {
            break-after: avoid;
            page-break-after: avoid;
          }
          /* Allow page breaks between sections */
          .print-section {
            break-before: auto;
            page-break-before: auto;
          }
          /* Table styling for print */
          table {
            border-collapse: collapse;
            width: 100%;
          }
          th, td {
            border: 1px solid #d1d5db;
            padding: 8px;
            text-align: left;
            vertical-align: top;
          }
          th {
            background-color: #f3f4f6 !important;
            font-weight: 600;
          }
          /* Alternating row colors for readability */
          tbody tr:nth-child(even) {
            background-color: #f9fafb !important;
          }
        }
        @page {
          size: ${isLandscape ? 'A4 landscape' : 'A4'};
          margin: 15mm;
        }
      `}</style>

      {/* Print Controls (hidden when printing) */}
      <div className="no-print fixed top-4 right-4 flex gap-2 z-50">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Afdrukken / PDF opslaan
        </button>
        <button
          onClick={onClose || (() => window.close())}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 shadow-lg"
        >
          Sluiten
        </button>
      </div>

      {/* Print Content */}
      <div className={`print-page bg-white min-h-screen p-8 ${isLandscape ? 'max-w-6xl' : 'max-w-4xl'} mx-auto`}>
        {/* Header */}
        <div className="border-b-2 border-blue-600 pb-4 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-blue-800">{title}</h1>
              {subtitle && <p className="text-gray-600 mt-1">{subtitle}</p>}
            </div>
            {showDate && (
              <div className="text-right">
                <p className="text-xs text-gray-500">
                  Afgedrukt: {new Date().toLocaleDateString('nl-NL')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        {children}

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-200 text-center text-xs text-gray-400">
          {footerContent || (
            <p>Functieloonhuis Beoordelingssysteem</p>
          )}
        </div>
      </div>
    </>
  );
}

export default PrintLayout;
